import { findWorkflowFunction } from './findWorkflowFunction.js';
import { cleanGraphForWorkflowRun } from '../graph/cleanGraphForWorkflowRun.js';
import { flattenGraphSequence } from '../graph/flattenGraphSequence.js';
import { getAllGraphStepChildrenIds } from '../graph/getAllGraphStepChildrenIds.js';
import { getAllParentGraphSteps } from '../graph/getAllParentGraphSteps.js';
import { findStepApp } from './findStepApp.js';
import { getWorkflowStepDependencies } from './getWorkflowStepDependencies.js';

/**
 * Decides whether an Iterate loop's body can run its items in parallel.
 *
 * Shared by the editor (feature box + slider), the pre-execution validator and the runtime
 * (`runIterativeStep`), so all three agree. A blocker means the runtime runs the loop one
 * item at a time and says why on the loop response; it never fails the run.
 *
 * Steps outside the loop cannot change while it runs, so the real hazards are state that
 * outlives one item (memory, key-value storage), control flow with run-wide meaning (break,
 * endAsSuccess, pagination that ends the workflow at its page limit), one-shot resources (the
 * webhook response) and references that can read the previous item's value: a body step at or
 * after the reader, or a step inside an inner loop that ran no items for this outer item.
 *
 * "Nearest enclosing loop" is the closest iterate or batch-pagination ancestor. `break` and
 * `continue` only reach that loop, so anything scoped to a sequential inner loop behaves the
 * same in both modes and is not a blocker.
 */
export const PARALLEL_LOOP_BLOCKER_CODES = Object.freeze({
  BREAK_STEP: 'BREAK_STEP',
  FLOW_CONTROL_STOPS_LOOP: 'FLOW_CONTROL_STOPS_LOOP',
  FLOW_CONTROL_OPTION_BREAKS: 'FLOW_CONTROL_OPTION_BREAKS',
  MEMORY_WRITE: 'MEMORY_WRITE',
  KEY_VALUE_READ_WRITE: 'KEY_VALUE_READ_WRITE',
  KEY_VALUE_UNVERIFIED: 'KEY_VALUE_UNVERIFIED',
  WEBHOOK_RESPONSE: 'WEBHOOK_RESPONSE',
  PAGINATION_ENDS_WORKFLOW: 'PAGINATION_ENDS_WORKFLOW',
  CARRIED_REFERENCE: 'CARRIED_REFERENCE',
  INNER_LOOP_REFERENCE: 'INNER_LOOP_REFERENCE',
  FUNCTION_UNSAFE: 'FUNCTION_UNSAFE'
});

const KEY_VALUE_READ_SLUGS = new Set(['getValue', 'listKeys', 'listNamespaces']);
const KEY_VALUE_WRITE_SLUGS = new Set(['setValue', 'removeKey', 'removeNamespace']);

const isLoopStep = (step) => step?.appSlug === 'iterate' || step?.options?.pagination?.type === 'batch';

// `handlePaginationCompletion` sets the run-wide `endAsSuccess` for this configuration, which ends
// the sequential loop with the current item; a parallel loop cannot honor that for its other lanes.
const endsWorkflowAtPageLimit = (step) => step?.options?.pagination?.enablePageLimit === true && step?.options?.pagination?.ifMaxPagesReached === 'end';

const isMemoryWrite = (step) => step?.appSlug === 'memory' || (Array.isArray(step?.options?.memory) && step.options.memory.length > 0) || (step?.options?.pagination?.type === 'batch' && step?.options?.pagination?.rememberPage === true);

const getKeyValueActionSlug = ({ step, apps }) => {
  if (step?.appSlug !== 'keyValueStorage') {
    return undefined;
  }
  const app = findStepApp(step, apps || []);
  return app?.actions?.find((action) => action?.id === step?.actionId)?.slug;
};

/**
 * Every Key-Value storage action reachable from the loop body: the body's own steps and, through
 * each function step, the called function's steps (recursively). Storage rows are shared by
 * company, namespace and key, so a call boundary does not isolate them. An action found inside a
 * function is attributed to the body step that calls it.
 */
const collectKeyValueEffects = ({ steps, apps, workflowFunctions, blameStepId, visited, effects }) => {
  for (const step of steps) {
    if (step?.appSlug === 'keyValueStorage') {
      effects.push({ stepId: blameStepId || step.id, slug: getKeyValueActionSlug({ step, apps }) });
    }
    if (step?.functionId) {
      const functionWorkflow = findWorkflowFunction({ step, workflowFunctions });
      if (functionWorkflow?.id && !visited.has(functionWorkflow)) {
        visited.add(functionWorkflow);
        collectKeyValueEffects({ steps: functionWorkflow.steps || [], apps, workflowFunctions, blameStepId: blameStepId || step.id, visited, effects });
      }
    }
  }
  return effects;
};

/**
 * Key-Value storage read-modify-write loses updates under concurrency. Reads and writes are
 * judged together over the whole call graph: a read in the body with the write inside a called
 * function is the same hazard as both in the body. Without the app catalog the action cannot
 * be told apart, which is reported honestly rather than guessed.
 */
const getKeyValueBlockers = ({ steps, apps, workflowFunctions }) => {
  const effects = collectKeyValueEffects({ steps, apps, workflowFunctions, blameStepId: null, visited: new Set(), effects: [] });
  if (effects.length === 0) {
    return [];
  }

  const unresolved = effects.filter((effect) => !effect.slug);
  if (unresolved.length > 0) {
    return unresolved.map(({ stepId }) => ({ stepId, code: PARALLEL_LOOP_BLOCKER_CODES.KEY_VALUE_UNVERIFIED }));
  }

  const hasRead = effects.some(({ slug }) => KEY_VALUE_READ_SLUGS.has(slug));
  const hasWrite = effects.some(({ slug }) => KEY_VALUE_WRITE_SLUGS.has(slug));
  if (!hasRead || !hasWrite) {
    return [];
  }

  return effects.map(({ stepId }) => ({ stepId, code: PARALLEL_LOOP_BLOCKER_CODES.KEY_VALUE_READ_WRITE }));
};

/**
 * Function steps run their own steps against their own state, so break/continue inside them
 * are contained, but memory is shared with the calling run. Key-value storage is judged over
 * the whole call graph by `getKeyValueBlockers`.
 */
const isFunctionUnsafe = ({ functionWorkflow, apps, workflowFunctions, visited }) => {
  if (!functionWorkflow?.id) {
    return true;
  }
  if (visited.has(functionWorkflow)) {
    return false;
  }
  visited.add(functionWorkflow);

  const steps = functionWorkflow.steps || [];
  if (steps.some(isMemoryWrite)) {
    return true;
  }

  return steps
    .filter((step) => step?.functionId)
    .some((step) => {
      const nested = findWorkflowFunction({ step, workflowFunctions });
      return isFunctionUnsafe({ functionWorkflow: nested, apps, workflowFunctions, visited });
    });
};

const sharesSiblingBranch = (branchPathA, branchPathB) => branchPathA.some((a) => branchPathB.some((b) => a.id === b.id && a.index !== b.index));

/**
 * Group Results, and a Map with grouping enabled for an input, read that input through
 * `state.groupedResponses`, which is indexed by the current item in both modes. An inner loop
 * that ran no items leaves an empty slot for the item either way, so those reads are safe.
 */
const isGroupedRead = ({ step, referencedStepId }) => {
  if (step?.appSlug === 'groupResults') {
    return true;
  }
  return step?.appSlug === 'map' && (step?.arguments?.inputStepIdParents?.[referencedStepId] || []).length > 0;
};

export const getParallelLoopBlockers = ({ workflow, graph, iterateStepId, apps, workflowFunctions = [] }) => {
  const steps = workflow?.steps || [];
  const runGraph = cleanGraphForWorkflowRun(graph || workflow?.graph || {}, steps);
  const childId = runGraph[iterateStepId]?.child;
  if (!childId || childId === iterateStepId) {
    return [];
  }

  const stepsById = new Map(steps.map((step) => [step?.id, step]));
  const bodyStepIds = getAllGraphStepChildrenIds(runGraph, childId, [childId]).filter((id) => stepsById.has(id));
  const bodySet = new Set(bodyStepIds);
  const bodySteps = bodyStepIds.map((id) => stepsById.get(id));

  const orderById = new Map();
  (flattenGraphSequence(runGraph, steps) || []).forEach((node, index) => {
    if (node?.id) {
      orderById.set(node.id, index);
    }
  });

  const parentsById = new Map();
  const getParents = (stepId) => {
    if (!parentsById.has(stepId)) {
      parentsById.set(
        stepId,
        getAllParentGraphSteps(stepId, runGraph).filter((parent) => !!parent?.id)
      );
    }
    return parentsById.get(stepId);
  };
  const getNearestLoopId = (stepId) => getParents(stepId).find((parent) => parent.relation === 'child' && isLoopStep(stepsById.get(parent.id)))?.id;
  const getBranchPath = (stepId) => getParents(stepId).filter((parent) => parent.relation === 'branch');
  // Loops between a body step and this loop, nearest first. Each may run zero items for an outer item.
  const getInnerLoopIds = (stepId) =>
    getParents(stepId)
      .filter((parent) => parent.relation === 'child' && parent.id !== iterateStepId && bodySet.has(parent.id) && isLoopStep(stepsById.get(parent.id)))
      .map((parent) => parent.id);

  const blockers = [];
  const push = (blocker) => {
    if (!blockers.some((existing) => existing.stepId === blocker.stepId && existing.code === blocker.code && existing.referencedStepId === blocker.referencedStepId)) {
      blockers.push(blocker);
    }
  };

  for (const step of bodySteps) {
    const stepId = step.id;
    const breaksThisLoop = getNearestLoopId(stepId) === iterateStepId;

    if (step.appSlug === 'break' && breaksThisLoop) {
      push({ stepId, code: PARALLEL_LOOP_BLOCKER_CODES.BREAK_STEP });
    }

    if (step.appSlug === 'flowControl') {
      const action = step.arguments?.action;
      if ((action === 'break' && breaksThisLoop) || action === 'endAsSuccess') {
        push({ stepId, code: PARALLEL_LOOP_BLOCKER_CODES.FLOW_CONTROL_STOPS_LOOP });
      }
    }

    const flowControl = step.options?.flowControl;
    if (breaksThisLoop && (flowControl?.onFailure?.action === 'break' || flowControl?.onNoResults?.action === 'break')) {
      push({ stepId, code: PARALLEL_LOOP_BLOCKER_CODES.FLOW_CONTROL_OPTION_BREAKS });
    }

    if (isMemoryWrite(step)) {
      push({ stepId, code: PARALLEL_LOOP_BLOCKER_CODES.MEMORY_WRITE });
    }

    if (step.appSlug === 'webhookResponse') {
      push({ stepId, code: PARALLEL_LOOP_BLOCKER_CODES.WEBHOOK_RESPONSE });
    }

    if (endsWorkflowAtPageLimit(step)) {
      push({ stepId, code: PARALLEL_LOOP_BLOCKER_CODES.PAGINATION_ENDS_WORKFLOW });
    }

    if (step.functionId) {
      const functionWorkflow = findWorkflowFunction({ step, workflowFunctions });
      if (isFunctionUnsafe({ functionWorkflow, apps, workflowFunctions, visited: new Set() })) {
        push({ stepId, code: PARALLEL_LOOP_BLOCKER_CODES.FUNCTION_UNSAFE });
      }
    }

    // A body step that reads a body step at or after its own position reads the previous
    // item's value. That holds inside a sequential inner loop too: on the inner loop's first
    // iteration the sequential engine hands over the previous OUTER item's value, which a
    // parallel item (own response scope) cannot see. A sibling If/Else lane is `null` in both
    // modes, so it is not a hazard.
    //
    // A read of a step inside an inner loop the reader is not part of is a hazard even when
    // that step comes first: the sequential engine never clears an inner loop's body when the
    // loop runs zero items, so the reader gets the previous outer item's value, while a parallel
    // item gets `null`. Both runs succeed, so only the fallback keeps the data the same.
    const parentSteps = getParents(stepId)
      .map((parent) => stepsById.get(parent.id))
      .filter(Boolean);
    const dependencies = getWorkflowStepDependencies(step, { parents: parentSteps }).filter((dependencyId) => bodySet.has(dependencyId));
    const stepOrder = orderById.get(stepId);
    const stepBranchPath = getBranchPath(stepId);
    const stepInnerLoopIds = new Set(getInnerLoopIds(stepId));

    for (const referencedStepId of dependencies) {
      const readsInnerLoopBody = getInnerLoopIds(referencedStepId).some((loopId) => !stepInnerLoopIds.has(loopId));
      if (readsInnerLoopBody && !isGroupedRead({ step, referencedStepId })) {
        push({ stepId, code: PARALLEL_LOOP_BLOCKER_CODES.INNER_LOOP_REFERENCE, referencedStepId });
        continue;
      }
      const referencedOrder = orderById.get(referencedStepId);
      if (!Number.isInteger(stepOrder) || !Number.isInteger(referencedOrder) || referencedOrder < stepOrder) {
        continue;
      }
      if (sharesSiblingBranch(stepBranchPath, getBranchPath(referencedStepId))) {
        continue;
      }
      push({ stepId, code: PARALLEL_LOOP_BLOCKER_CODES.CARRIED_REFERENCE, referencedStepId });
    }
  }

  getKeyValueBlockers({ steps: bodySteps, apps, workflowFunctions }).forEach(push);

  return blockers;
};

export default getParallelLoopBlockers;
